import React from 'react'
import { useDraggable } from '@dnd-kit/core'
import { TaskEntry } from '../../../../components/TaskEntry'
import type { TaskEntryProps } from '../../../../components/TaskEntry'
import './DraggableTaskEntry.scss'

interface DraggableTaskEntryProps extends TaskEntryProps {
  /** Task id — used as the dnd-kit draggable identifier. */
  taskId: string
}

/**
 * Wraps a collapsed TaskEntry as a drag source for the UpcomingPage's
 * page-level DndContext. The real drag preview lives in the DragOverlay;
 * while dragging, the source dims to 40 % so the user sees "this item is
 * being moved" without the visual jitter of transform-based sortables.
 *
 * Disabled while the task is being edited (mode === 'expanded') — otherwise
 * the activation distance + text input focus would fight each other.
 */
export const DraggableTaskEntry: React.FC<DraggableTaskEntryProps> = ({
  taskId,
  ...taskEntryProps
}) => {
  const isDisabled = taskEntryProps.mode === 'expanded' || taskEntryProps.mode === 'creating'

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: taskId,
    disabled: isDisabled,
  })

  return (
    <div
      ref={setNodeRef}
      className={`draggable-task-entry${isDragging ? ' draggable-task-entry--dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      <TaskEntry {...taskEntryProps} />
    </div>
  )
}
